/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import type { FC } from 'react';
import { CustomTooltip, FilterChip } from '@opengeoweb/shared';
import ExpandableFilterChip from './ExpandableFilterChip';
import type { PickedFilterState, UpdateFilter } from './filter-hooks';
import type { TimeFilter } from './filter-utils';
import TimeFilterChip from './TimeFilterChip';
import { useWarningsTranslation } from '../../utils/i18n';

const filterOrder: Exclude<keyof PickedFilterState, 'time'>[] = [
  'source',
  'domain',
  'phenomenon',
  'level',
];
interface FilterListProps {
  onClickAll?: (isChecked: boolean) => void;
  isAllSelected?: boolean;
  filterState: PickedFilterState;
  updateFilter: UpdateFilter;
  timeFilter: TimeFilter;
  onUpdateTimeFilter: (timeFilter: TimeFilter) => void;
}

const FilterList: FC<FilterListProps> = ({
  onClickAll = (): void => {},
  isAllSelected = true,
  filterState,
  updateFilter,
  timeFilter,
  onUpdateTimeFilter,
}: FilterListProps) => {
  const { t } = useWarningsTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        pb: 4,
      }}
    >
      <CustomTooltip
        placement="bottom"
        title={isAllSelected ? t('filter-hide-all') : t('filter-show-all')}
      >
        <div>
          <FilterChip
            handleClick={(): void => onClickAll(isAllSelected)}
            id="all"
            isSelected={isAllSelected}
            label={t('filter-all')}
            size="medium"
          />
        </div>
      </CustomTooltip>
      {filterOrder.map((filterId) => {
        return (
          <ExpandableFilterChip
            id={filterId}
            key={filterId}
            options={filterState[filterId]}
            updateFilter={updateFilter}
          />
        );
      })}
      <Box sx={{ marginLeft: 'auto' }}>
        <TimeFilterChip
          onUpdateTimeFilter={onUpdateTimeFilter}
          timeFilter={timeFilter}
        />
      </Box>
    </Box>
  );
};

export default FilterList;
